//#region libraries
import React, { useEffect, useMemo, useState } from "react";


// custom components
import { Card, CustomSpinner } from "_atoms";
import { TotalCards } from "_organisms";

// bootstrap
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import Form from "react-bootstrap/Form";
import { DateTime } from "_atoms";
import { setDate, selectDateToISO } from "_redux/slices/prm-bookings/calendar";

// redux
import { useSelector, useDispatch } from "react-redux";
import {
  selectDeparturesArrivalsCountByHour,
  getAnalyticsCountInboundByHour,
  getAnalyticsCountOutboundByHour,
  getAnalyticsStats,
  setTypeSelector,
  selectAnalyticsTypeSelector,
  selectAnalyticsStats,
  getAnalyticsCountOutboundByHourNoReduce,
  getBookingsDueTimes,
  selectDeparturesArrivalsCountByQuarter,
  getInboundDocuments, getOutboundDocuments
} from "_redux/slices/prm-bookings/bookings-count";
import { selectIata, selectUserDb } from "_redux/slices/main-screen/user";
import {
  selectForecast
} from "_redux/slices/analytics/weather";

import {
  getHourlyForecast,
  selectForecastByHour,
  selectForecastByQuarter,
} from "_redux/slices/security/security";

import {
  getAverageDueTime
} from "_redux/slices/analytics/reviews";
// exterior libraries
import { add, format, addDays, parseISO } from "date-fns";

// utils
import { lookupIsLoading } from "_utils/statusUtil";

// services
import { getHourlyForecast as fetchForecastForDate } from "_services/bookingAnalytics-service";



// tremor librarues
import {
  ColGrid,
  Flex,
  Title,
  Subtitle,
  ToggleItem,
  Toggle,
  Col as TremorCol,
} from "@tremor/react";
import { BarChart } from "@tremor/react";

// icons
import { selectAirlineSelectedAirport } from "_redux/slices/prm-bookings/bookings";
//#endregion

//#region components
const dataFormatter = (number) => {
  return Intl.NumberFormat("us").format(number).toString();
};
//#endregion

const DailyAnalytics = (props) => {

  //#region redux
  const dispatch = useDispatch();
  const analyticsDate = useSelector(selectDateToISO); // start date of the week
  const analyticsTypeSelector = useSelector(selectAnalyticsTypeSelector); // 'source' (app/sita/portal) or 'none' (all bookings)
  const analyticsStats = useSelector(selectAnalyticsStats);
  const userDb = useSelector(selectUserDb);
  const iata = useSelector(selectIata);
  const airport = useSelector(selectAirlineSelectedAirport);
  const weatherForecast = useSelector(selectForecast);
  const splitData = useSelector(selectDeparturesArrivalsCountByHour);
  const realCountByQuarter = useSelector(selectDeparturesArrivalsCountByQuarter);

  const realTimeData = useMemo(() => {
    if (!splitData || splitData.length === 0) return [];
    const offset = 2;
    return splitData.map((item, i) => ({
      ...item,
      Departures: i + offset < splitData.length ? splitData[i + offset].Departures : 0,
    }));
  }, [splitData]);

  const isSecurity = userDb?.role === "SECURITY";
  const forecastByHour = useSelector(selectForecastByHour);
  const forecastByQuarter = useSelector(selectForecastByQuarter);

  const securityHourlyData = useMemo(() => {
    if (!splitData || splitData.length === 0) return [];
    return splitData.map((item, i) => ({
      name: item.name,
      Departures: item.Departures,
      Forecast: forecastByHour?.[i]?.Forecast ?? 0,
    }));
  }, [splitData, forecastByHour]);

  const securityHourlyRealTime = useMemo(() => {
    if (!securityHourlyData || securityHourlyData.length === 0) return [];
    const offset = 2;
    return securityHourlyData.map((item, i) => ({
      ...item,
      Departures: i + offset < securityHourlyData.length ? securityHourlyData[i + offset].Departures : 0,
      Forecast: i + offset < securityHourlyData.length ? securityHourlyData[i + offset].Forecast : 0,
    }));
  }, [securityHourlyData]);

  const securityQuarterlyData = useMemo(() => {
    if (!realCountByQuarter || realCountByQuarter.length === 0) return [];
    return realCountByQuarter.map((item, i) => ({
      name: item.name,
      Departures: item.Departures,
      Forecast: forecastByQuarter?.[i]?.Forecast ?? 0,
    }));
  }, [realCountByQuarter, forecastByQuarter]);

  const securityQuarterlyRealTime = useMemo(() => {
    if (!securityQuarterlyData || securityQuarterlyData.length === 0) return [];
    const offset = 8; // 2 hours × 4 quarters
    return securityQuarterlyData.map((item, i) => ({
      ...item,
      Departures: i + offset < securityQuarterlyData.length ? securityQuarterlyData[i + offset].Departures : 0,
      Forecast: i + offset < securityQuarterlyData.length ? securityQuarterlyData[i + offset].Forecast : 0,
    }));
  }, [securityQuarterlyData]);

  const isLoading = useSelector(state =>
    lookupIsLoading(state.count.statusObj, [
      "getAnalyticsStats",
      "getAnalyticsCountOutboundByHour",
      "getAnalyticsCountInboundByHour",
      "getAnalyticsCountOutboundByHourNoReduce",
    ]) || state.count.bookingsDueTimesStatus === "loading"
  );
  //end region

  //state region
  const [showWeather, setShowWeather] = useState(false); //state show,hide weather
  const [dataType, setDataType] = useState("Flight Time");
  const [granularity, setGranularity] = useState("Hourly");
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportStartDate, setExportStartDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [exportEndDate, setExportEndDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [isExporting, setIsExporting] = useState(false);

  const handleExportForecast = async () => {
    setIsExporting(true);
    try {
      const Excel = await import("exceljs/dist/exceljs.min.js");
      const workbook = new Excel.Workbook();

      const start = parseISO(exportStartDate);
      const end = parseISO(exportEndDate);
      const dayCount = Math.round((end - start) / (1000 * 60 * 60 * 24)) + 1;

      for (let d = 0; d < dayCount; d++) {
        const currentDate = addDays(start, d);
        const dateStr = format(currentDate, "yyyy-MM-dd");

        let hourly;
        try {
          hourly = await fetchForecastForDate({ iata, date: dateStr });
        } catch {
          hourly = new Array(24).fill(0);
        }

        const sheet = workbook.addWorksheet(dateStr);
        sheet.columns = [
          { header: "Hour", key: "hour", width: 10 },
          { header: "Forecasted Pax", key: "pax", width: 18 },
        ];

        for (let h = 0; h < 24; h++) {
          sheet.addRow({ hour: `${h.toString().padStart(2, "0")}:00`, pax: Math.round(hourly[h] || 0) });
        }

        sheet.getRow(1).font = { bold: true };
      }

      const buf = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `forecast_${exportStartDate}_to_${exportEndDate}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setShowExportModal(false);
    } catch (e) {
      console.error("[Forecast Export] Error:", e);
      alert("Error exporting forecast data");
    } finally {
      setIsExporting(false);
    }
  };

  //#endregion

  //use Effect region
  useEffect(() => {
    if (isSecurity && analyticsTypeSelector !== "none") {
      dispatch(setTypeSelector("none"));
    }
  }, [isSecurity, analyticsTypeSelector, dispatch]);

  useEffect(() => {
    console.log('[Forecast] useEffect check:', { isSecurity, iata, analyticsDate });
    if (isSecurity && iata && analyticsDate) {
      console.log('[Forecast] Dispatching getHourlyForecast:', { iata, date: analyticsDate });
      dispatch(getHourlyForecast({ iata, date: analyticsDate }));
    }
  }, [isSecurity, iata, analyticsDate, dispatch]);

  useEffect(() => {
    const isValidDate =
      analyticsDate &&
      analyticsDate !== "1970-01-01" &&
      !Number.isNaN(new Date(analyticsDate).getTime());

    if (!isValidDate) {
      dispatch(setDate(format(new Date(), "yyyy-MM-dd")));
    }
  }, [analyticsDate, dispatch]);

  // get inbound documents used for quarterly graph
  useEffect(() => {
    dispatch(getInboundDocuments({
      iata: iata,
      start: analyticsDate,
      end: analyticsDate,
      limit:10000,
      skip:0,
      search: "",
      type:'inbound'
    }));
    dispatch(getOutboundDocuments({
      iata: iata,
      start: analyticsDate,
      end: analyticsDate,
      limit:10000,
      skip:0,
      search: "",
      type:'outbound'
  }));
  }, []);

  useEffect(() => {
    // console.log('splitData', splitData)
  }, [splitData]);

  useEffect(() => {
    let countParams = {
      iata: userDb.details.type == "AIRLINE" ? airport : iata,
      date: analyticsDate,
      endDate: add(new Date(analyticsDate), { days: 0 })
        .toISOString()
        .split("T")[0],
      source: analyticsTypeSelector,
      airline:
        userDb.details.type == "AIRLINE" ? userDb.details.authority : null,
      noshows: true,
    };
    const dueTimeData = {
      iata: userDb.details.type == "AIRLINE" ? airport : iata,
      date: analyticsDate,
      endDate: add(new Date(analyticsDate), { days: 0 })
        .toISOString()
        .split("T")[0],
    }
    if (userDb.details.type !== "AIRLINE") {
      dispatch(
        getBookingsDueTimes(dueTimeData)
      );
    }
    dispatch(getAnalyticsCountInboundByHour(countParams));
    dispatch(getAnalyticsCountOutboundByHour({ ...countParams }));
    dispatch(getAnalyticsCountOutboundByHourNoReduce({ ...countParams }));
    dispatch(getAnalyticsStats(countParams));
    if (userDb.details.type !== "AIRLINE") {
      dispatch(
        getAverageDueTime({
          departureAirport: userDb.details.type == "AIRLINE" ? airport : iata,
          arrivalAirport: "",
          startDate: analyticsDate,
          endDate: add(new Date(analyticsDate), { days: 0 })
            .toISOString()
            .split("T")[0],
          filter: analyticsTypeSelector,
        })
      );
    }
  }, [analyticsTypeSelector, analyticsDate, airport]);

  useEffect(() => {
    if (weatherForecast && weatherForecast[analyticsDate]) {
      setShowWeather(true);
    }
  }, [weatherForecast]);

  //end region
  return (
    <>
      <ColGrid>

        {/* Type Filter && Date Picker */}
        <Row className={`mt-3 align-items-center ${isSecurity ? "justify-content-end" : "justify-content-between"}`}>
          {!isSecurity && (
            <Col xs="auto">
              <Toggle
                color="zinc"
                defaultValue={analyticsTypeSelector}
                onValueChange={(value) => dispatch(setTypeSelector(value))}
              >
                <ToggleItem value={"none"} text="All Bookings" />
                <ToggleItem value={"P"} text="Portal" />
                <ToggleItem value={"A"} text="App" />
                <ToggleItem value={"S"} text="SITA" />
                <ToggleItem value={"C"} text="Connect API" />
              </Toggle>
            </Col>
          )}
          <Col xs="auto">
            <DateTime
              isBooking={true}
              date={analyticsDate}
              setDate={(date) => {
                dispatch(setDate(date));
              }}
            />
          </Col>
          {isSecurity && (
            <Col xs="auto">
              <Button variant="outline-primary" onClick={() => setShowExportModal(true)}>
                Export Forecast
              </Button>
            </Col>
          )}
        </Row>

        {isLoading ? (
          <CustomSpinner rowCenter={true} />
        ) : (
          <>
            {/* total analytics */}
            {analyticsStats && <TotalCards showWeather={showWeather} />}

            {
              <ColGrid
                numColsMd={1}
                numColsLg={6}
                gapX="gap-x-6"
                gapY="gap-y-6"
                marginTop="mt-6"
              >
                <TremorCol numColSpanLg={6}>
                  <Card>
                    <Row>
                      <Col>
                        <Flex>
                          <div>
                            <Title>Booking Graph</Title>
                            <Subtitle>
                              {isSecurity
                                ? "View actual departures vs forecasted departures per hour"
                                : "View the number of departures and arrivals per hour"}
                            </Subtitle>
                          </div>
                          {
                            // show if not airline account
                            userDb.details.type != "AIRLINE" && (
                              <>
                                <Toggle
                                  color="zinc"
                                  defaultValue={dataType}
                                  onValueChange={(value) => setDataType(value)}
                                >
                                  <ToggleItem
                                    value={"Flight Time"}
                                    text="Flight Time"
                                  />
                                  <ToggleItem value={"Real Time"} text="Real Time" />
                                </Toggle>
                                <Toggle
                                  color="zinc"
                                  defaultValue={granularity}
                                  onValueChange={(value) => setGranularity(value)}
                                  style={{ marginLeft: 8 }}
                                >
                                  <ToggleItem value={"Hourly"} text="Hourly" />
                                  <ToggleItem value={"Quarterly"} text="Quarterly" />
                                </Toggle>
                              </>
                            )
                          }
                        </Flex>
                      </Col>
                    </Row>
                    <Row>
                      <BarChart
                        data={
                          isSecurity
                            ? (dataType === "Flight Time"
                              ? (granularity === "Quarterly" ? securityQuarterlyData : securityHourlyData)
                              : (granularity === "Quarterly" ? securityQuarterlyRealTime : securityHourlyRealTime))
                            : (dataType === "Flight Time"
                              ? (granularity === "Quarterly" ? realCountByQuarter : splitData)
                              : (granularity === "Quarterly" ? realCountByQuarter : realTimeData))
                        }
                        dataKey="name"
                        categories={isSecurity ? ["Departures", "Forecast"] : ["Departures", "Arrivals"]}
                        colors={isSecurity ? ["blue", "cyan"] : ["blue", "green"]}
                        marginTop="mt-5"
                        yAxisWidth="w-12"
                        showAnimation={false}
                        valueFormatter={dataFormatter}
                      />
                    </Row>
                  </Card>
                </TremorCol>
              </ColGrid>
            }
          </>
        )}
      </ColGrid>

      {/* Export Forecast Modal */}
      <Modal show={showExportModal} onHide={() => setShowExportModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Export Forecast</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Start Date</Form.Label>
            <Form.Control
              type="date"
              value={exportStartDate}
              onChange={(e) => setExportStartDate(e.target.value)}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>End Date</Form.Label>
            <Form.Control
              type="date"
              value={exportEndDate}
              onChange={(e) => setExportEndDate(e.target.value)}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowExportModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleExportForecast} disabled={isExporting}>
            {isExporting ? "Exporting..." : "Export"}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default DailyAnalytics;
